import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { apiPut } from "../../utils/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { AdminPanel, AdminBtnPrimary } from "./AdminUI";
import { Colors } from "../../utils/utils.colors";
import {
  computeFeeTotal,
  normalizeFeeRows,
  FEE_DETAIL_LABELS,
  FEE_TRANCHE_LABELS,
} from "../../utils/fees";

type DraftDetails = { [K in keyof DetailsFrais]: number | null };
type DraftTranches = { [K in keyof Tranches]: number | null };

type FeeDraft = {
  id?: number;
  key: number;
  promotion: string;
  faculte: string;
  details_frais: DraftDetails;
  tranches: DraftTranches;
};

type Props = {
  rows: ItemFrais[];
  onSaved: (rows: ItemFrais[]) => void;
};

const emptyDetails = (): DraftDetails => ({
  frais_academiques: 0,
  releve: 0,
  reinscription: 0,
  carte: 0,
  bibliotheque: 0,
  coordination: 0,
  diplome: 0,
  construction: 0,
  stage: 0,
  jury: 0,
});

const emptyTranches = (): DraftTranches => ({
  premiere: 0,
  deuxieme: 0,
  troisieme: 0,
});

const toDraft = (row: ItemFrais, index: number): FeeDraft => ({
  id: row.id,
  key: row.key || index + 1,
  promotion: row.promotion ?? "",
  faculte: row.faculte ?? "",
  details_frais: { ...(row.details_frais as unknown as DraftDetails) },
  tranches: { ...(row.tranches as unknown as DraftTranches) },
});

const NumberInput: React.FC<{
  value: number | null;
  onChange: (value: number | null) => void;
  ariaLabel: string;
  testId?: string;
}> = ({ value, onChange, ariaLabel, testId }) => (
  <input
    type="number"
    className="admin-fee-input"
    value={value === null || value === undefined ? "" : value}
    onChange={(e) => {
      const raw = e.target.value;
      if (raw === "") {
        onChange(null);
        return;
      }
      const n = Number(raw);
      if (Number.isFinite(n)) onChange(n);
    }}
    aria-label={ariaLabel}
    data-testid={testId}
  />
);

export const AdminFeesPanel: React.FC<Props> = ({ rows, onSaved }) => {
  const { t } = useLanguage();
  const [drafts, setDrafts] = useState<FeeDraft[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDrafts((rows || []).map((row, index) => toDraft(row, index)));
  }, [rows]);

  const updateDraft = (index: number, patch: Partial<FeeDraft>) => {
    setDrafts((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const updateDetail = (index: number, key: keyof DetailsFrais, value: number | null) => {
    setDrafts((prev) =>
      prev.map((row, i) =>
        i === index
          ? { ...row, details_frais: { ...row.details_frais, [key]: value } }
          : row
      )
    );
  };

  const updateTranche = (index: number, key: keyof Tranches, value: number | null) => {
    setDrafts((prev) =>
      prev.map((row, i) =>
        i === index ? { ...row, tranches: { ...row.tranches, [key]: value } } : row
      )
    );
  };

  const addRow = () => {
    const nextKey = drafts.reduce((max, row) => Math.max(max, row.key || 0), 0) + 1;
    setDrafts((prev) => [
      ...prev,
      {
        key: nextKey,
        promotion: "",
        faculte: "",
        details_frais: emptyDetails(),
        tranches: emptyTranches(),
      },
    ]);
  };

  const removeRow = (index: number) => {
    if (!window.confirm("Supprimer cette ligne de frais ?")) return;
    setDrafts((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiPut("/fees", {
        rows: drafts.map((row) => ({
          id: row.id,
          promotion: row.promotion,
          faculte: row.faculte,
          details_frais: row.details_frais,
          tranches: row.tranches,
        })),
      });
      const saved = normalizeFeeRows(res?.rows ?? []);
      setDrafts(saved.map((row, index) => toDraft(row, index)));
      onSaved(saved);
      toast.success(t.admin.academicFeesSaved);
    } catch (e: any) {
      toast.error(e?.messageText || t.common.error);
    } finally {
      setSaving(false);
    }
  };

  const totals = useMemo(
    () => drafts.map((row) => computeFeeTotal(row.details_frais)),
    [drafts]
  );

  return (
    <AdminPanel>
      <div style={{ padding: "20px 24px 24px" }}>
        <p style={{ color: "#666", fontSize: 13, marginTop: 0 }}>
          {t.admin.academicFeesDesc}
        </p>

        <div className="admin-table-wrap" style={{ overflowX: "auto" }}>
          <table className="admin-table admin-fee-table" style={{ minWidth: 1180 }}>
            <thead>
              <tr>
                <th style={{ minWidth: 90 }}>{t.admin.academicFeesPromotion}</th>
                <th style={{ minWidth: 170 }}>{t.admin.academicFeesFaculty}</th>
                {FEE_DETAIL_LABELS.map(({ key, short }) => (
                  <th key={key} style={{ minWidth: 78 }} title={short}>
                    {short}
                  </th>
                ))}
                <th style={{ minWidth: 90, background: "#eef2ff", color: Colors.primaryColor }}>
                  {t.admin.academicFeesTotal}
                </th>
                {FEE_TRANCHE_LABELS.map(({ key, label }) => (
                  <th key={key} style={{ minWidth: 84 }}>
                    {label}
                  </th>
                ))}
                <th style={{ minWidth: 60 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {drafts.map((row, index) => (
                <tr key={row.id ?? `new-${row.key}`} data-testid={`fee-row-${index}`}>
                  <td>
                    <input
                      className="admin-fee-input admin-fee-input--text"
                      value={row.promotion}
                      onChange={(e) => updateDraft(index, { promotion: e.target.value })}
                      placeholder="L1"
                      aria-label={`${t.admin.academicFeesPromotion} ${index + 1}`}
                      data-testid={`fee-promotion-${index}`}
                    />
                  </td>
                  <td>
                    <input
                      className="admin-fee-input admin-fee-input--text"
                      value={row.faculte}
                      onChange={(e) => updateDraft(index, { faculte: e.target.value })}
                      placeholder="FST"
                      aria-label={`${t.admin.academicFeesFaculty} ${index + 1}`}
                      data-testid={`fee-faculte-${index}`}
                    />
                  </td>
                  {FEE_DETAIL_LABELS.map(({ key, label }) => (
                    <td key={key}>
                      <NumberInput
                        value={row.details_frais[key]}
                        onChange={(value) => updateDetail(index, key, value)}
                        ariaLabel={`${label} ${row.promotion} ${index + 1}`}
                        testId={`fee-${key}-${index}`}
                      />
                    </td>
                  ))}
                  <td
                    style={{
                      background: "#eef2ff",
                      textAlign: "center",
                      fontWeight: 700,
                      color: Colors.primaryColor,
                    }}
                    data-testid={`fee-total-${index}`}
                  >
                    {totals[index]}$
                  </td>
                  {FEE_TRANCHE_LABELS.map(({ key, label }) => (
                    <td key={key}>
                      <NumberInput
                        value={row.tranches[key]}
                        onChange={(value) => updateTranche(index, key, value)}
                        ariaLabel={`${label} ${row.promotion} ${index + 1}`}
                        testId={`fee-${key}-${index}`}
                      />
                    </td>
                  ))}
                  <td>
                    <button
                      type="button"
                      onClick={() => removeRow(index)}
                      className="admin-btn admin-btn--icon admin-btn--icon--danger"
                      title={t.admin.academicFeesDeleteRow}
                      aria-label={t.admin.academicFeesDeleteRow}
                      data-testid={`fee-delete-${index}`}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              ))}
              {drafts.length === 0 && (
                <tr>
                  <td colSpan={16} style={{ textAlign: "center", color: "#888", padding: 24 }}>
                    {t.admin.academicFeesEmpty}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div
          style={{
            marginTop: 16,
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <button type="button" className="admin-btn" onClick={addRow} data-testid="fee-add-row">
            + {t.admin.academicFeesAddRow}
          </button>

          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <span style={{ fontSize: 12, color: "#666" }}>{t.admin.academicFeesTotalHint}</span>
            <AdminBtnPrimary onClick={handleSave} disabled={saving} data-testid="fee-save">
              {saving ? "..." : t.admin.save}
            </AdminBtnPrimary>
          </div>
        </div>
      </div>
    </AdminPanel>
  );
};
